import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Reveal from "@/components/base/Reveal";
import Counter from "@/components/base/Counter";
import usePageMeta from "@/hooks/usePageMeta";

const HERO_IMG =
  "https://readdy.ai/api/search-image?query=Elegant%20dark%20navy%20blue%20abstract%20fluid%20background%20with%20warm%20soft%20orange%20light%20waves%20and%20smooth%20rounded%20organic%20shapes%2C%20gentle%20translucent%20curves%20flowing%20across%20the%20right%20side%2C%20soft%20bokeh%20circles%2C%20minimalist%20premium%20aesthetic%2C%20modern%20editorial%20style%2C%20no%20hard%20edges%2C%20dreamy%20atmospheric%20depth%2C%20high%20end%20magazine%20quality%2C%20subtle%20warm%20glow%20blending%20into%20deep%20navy&width=1200&height=750&seq=svw-hero-01&orientation=landscape&nocache=true";

const features = [
  {
    icon: "ri-palette-line",
    title: "Design sur-mesure",
    text: "Chaque site est pensé autour de votre univers, de vos couleurs et de votre clientèle locale.",
  },
  {
    icon: "ri-rocket-2-line",
    title: "Mise en ligne rapide",
    text: "Votre site vitrine est prêt et publié en moins de 10 jours, sans mauvaise surprise.",
  },
  {
    icon: "ri-smartphone-line",
    title: "100% Responsive",
    text: "Affichage impeccable sur mobile, tablette et ordinateur — là où sont vos clients.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Hébergement & entretien sécurisé",
    text: "Hébergement fiable et entretien inclus avec certificat SSL, sauvegarde et mise à jour, à 49,99 € par année.",
  },
  {
    icon: "ri-global-line",
    title: "Nom de domaine",
    text: "Un nom de domaine personnalisé, avec un QR code pour récolter des avis Google qui booste votre visibilité.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "Accompagnement humain",
    text: "Un interlocuteur unique, joignable, qui parle votre langue — pas la nôtre.",
  },
];

const steps = [
  { n: "01", t: "Échange", d: "Un échange approfondi pour cerner votre activité, votre clientèle et vos ambitions." },
  { n: "02", t: "Maquette", d: "Une maquette vous est présentée pour validation avant toute mise en ligne." },
  { n: "03", t: "Création", d: "Votre site vitrine est conçu dans le détail, textes et photos intégrés avec soin." },
  { n: "04", t: "Publication", d: "Mise en ligne, prise en main avec suivi tout au long de l'année si vous optez pour notre formule hébergement et entretien." },
];

const faq = [
  {
    q: "Combien de temps pour créer mon site ?",
    a: "En moyenne 7 à 10 jours ouvrés à compter de la validation de la maquette et de la réception de vos contenus.",
  },
  {
    q: "Qu'est-ce qui est inclus dans les 497,99€ ?",
    a: "La conception, la maquette, l'intégration jusqu'à 5 pages, la mise en ligne, un formulaire de contact et le référencement de base.",
  },
  {
    q: "Et les 49,99€ par an ?",
    a: "L'hébergement sécurisé, le nom de domaine, le certificat SSL, les sauvegardes et les petites retouches annuelles.",
  },
  {
    q: "Puis-je modifier mon site plus tard ?",
    a: "Bien sûr. Un petit lot d'ajustements est offert chaque année, au-delà nous proposons un tarif horaire simple et transparent.",
  },
];

const stats = [
  { value: 100, prefix: "Plus de ", suffix: "", label: "Commerces accompagnés" },
  { value: 10, prefix: "", suffix: " j", label: "Délai moyen de livraison" },
  { value: 100, prefix: "", suffix: "%", label: "Sur-mesure" },
];

export default function Home() {
  const location = useLocation();

  usePageMeta({
    title: "ShopVitrineWeb - Création de sites web pour commerçants locaux",
    description: "ShopVitrineWeb crée des sites vitrines professionnels et élégants pour les commerçants locaux : 497,99€ de création, 49,99€/an d'hébergement. Fleuristes, libraires, boulangers et plus.",
  });

  useEffect(() => {
    if (location.hash === "#assistant-ia") {
      const el = document.getElementById("assistant-ia");
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [location]);

  return (
    <Layout transparentNav>
      {/* HERO */}
      <section className="relative min-h-[820px] md:min-h-[920px] w-full overflow-hidden flex items-center py-16 md:py-24">
        <img
          src={HERO_IMG}
          alt="Bureau moderne d'agence web"
          className="absolute inset-0 w-full h-full object-cover object-top"
          fetchpriority="high"
          decoding="async"
        />
        {/* Dégradé 1 — noir quasi-solide jusqu'à ~45% puis transition vers bleu */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom right, rgba(4,7,13,0.96) 0%, rgba(4,7,13,0.94) 40%, rgba(4,7,13,0.85) 55%, rgba(27,46,78,0.65) 80%, rgba(27,46,78,0.45) 100%)",
          }}
        />
        {/* Dégradé 2 — noir jusqu'à la hauteur du titre (~40%), puis transition progressive vers bleu jusqu'en bas */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, #04070d 0%, #04070d 40%, rgba(27,46,78,0.70) 70%, rgba(27,46,78,0.50) 100%)",
          }}
        />

        {/* Right-side organic wave shapes — attenuated */}
        <div className="absolute top-0 right-0 h-full w-24 md:w-32 bg-coral-700/8 blur-[50px] rounded-[40%_60%_50%_50%]"></div>
        <div className="absolute top-1/4 right-0 w-72 h-56 md:w-96 md:h-72 bg-coral-600/6 blur-[60px] rounded-[60%_40%_70%_30%]"></div>
        <div className="absolute top-1/2 right-0 w-80 h-80 md:w-[28rem] md:h-[28rem] bg-coral-700/5 blur-[70px] rounded-[50%_50%_40%_60%]"></div>
        <div className="absolute bottom-1/4 right-0 w-64 h-72 md:w-80 md:h-96 bg-coral-500/5 blur-[50px] rounded-[40%_60%_60%_40%]"></div>

        <div className="relative z-10 w-full">
          <div className="mx-auto max-w-6xl px-6 md:px-10 w-full">
            <Reveal>
              <div className="flex flex-col lg:flex-row lg:items-start gap-10 lg:gap-14 text-white">
                <div className="flex-1 min-w-0">
                <h1 className="font-cormorant text-4xl md:text-6xl leading-[1.1] tracking-tight">
                  Votre vitrine mérite
                  <br />
                  <span className="text-white">un beau site internet.</span>
                </h1>
                <p className="mt-6 text-lg text-white max-w-2xl leading-relaxed">
                  ShopVitrineWeb conçoit des sites pour les commerçants.
                  Un travail soigné, un tarif clair, un vrai accompagnement.
                </p>

                <div className="mt-8">
                  <Link
                    to="/contact"
                    className="relative block w-full rounded-2xl border border-white/20 backdrop-blur-xl bg-navy-800/35 p-6 md:p-8 cursor-pointer transition-all duration-300 hover:border-coral-300/50 hover:bg-navy-800/50 overflow-hidden"
                  >
                    <h2 className="font-display text-xl text-coral-300">Notre offre</h2>
                    <div className="mt-4 space-y-3 text-sm">
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-white">Création du site</span>
                        <span className="font-semibold text-white whitespace-nowrap text-base" style={{ textShadow: "0 0 5px rgba(255,255,255,0.2), 0 0 12px rgba(255,255,255,0.08)" }}>497,99€</span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-white">Hébergement & entretien annuel</span>
                        <span className="font-semibold text-white whitespace-nowrap text-base" style={{ textShadow: "0 0 5px rgba(255,255,255,0.2), 0 0 12px rgba(255,255,255,0.08)" }}>49,99€ / an</span>
                      </div>
                    </div>
                    <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-white/10 border border-coral-300/40 p-3">
                      <span className="shrink-0 inline-flex items-center gap-1 rounded-full coral-shine bg-coral-400 text-navy-900 text-[10px] font-bold uppercase tracking-wide px-2 py-1 whitespace-nowrap">
                        <i className="ri-gift-line"></i>
                        Offert
                      </span>
                      <p className="text-xs text-white leading-relaxed">
                        Pour toute souscription d'un site avec hébergement, <span className="font-semibold text-white">QR code pour avis Google offert</span> d'une valeur de 29,99 €.
                      </p>
                    </div>
                  </Link>

                </div>

                <div className="mt-6 flex flex-wrap gap-4 text-sm text-white">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3.5 py-1.5 border border-white/10"><i className="ri-check-line text-coral-300"></i> Sans engagement</div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3.5 py-1.5 border border-white/10"><i className="ri-check-line text-coral-300"></i> Devis en 48h</div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3.5 py-1.5 border border-white/10"><i className="ri-check-line text-coral-300"></i> Basé en France</div>
                  <Link
                    to="/realisations"
                    className="inline-flex items-center gap-2 rounded-full coral-shine bg-coral-400 hover:bg-coral-500 text-navy-900 font-semibold px-3.5 py-1.5 text-sm border border-white/10 whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-gallery-line"></i>
                    Nos créations
                  </Link>
                </div>
                </div>

                <div className="lg:self-center lg:ml-auto shrink-0 lg:max-w-xs w-full">
                  <div className="flex items-end gap-4">
                    <span className="font-display text-6xl md:text-7xl leading-none text-coral-300">72%</span>
                    <span className="pb-1.5 text-white text-sm md:text-base leading-snug">
                      des professionnels constatent que le web booste leur activité
                    </span>
                  </div>
                  <p className="mt-6 text-white/85 text-base leading-relaxed">
                    Et pourtant, <span className="text-white font-semibold">1 commerce sur 2</span> n'a toujours pas de site internet.
                  </p>

                  <div className="mt-8 lg:mt-40 flex flex-wrap items-center gap-3">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 rounded-md coral-shine bg-coral-400 hover:bg-coral-500 text-navy-900 font-semibold px-4 py-2 text-sm whitespace-nowrap cursor-pointer transition-colors"
                    >
                      Demander mon site
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-navy-50 border-y border-navy-100">
        <Reveal className="mx-auto max-w-6xl px-6 md:px-10 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-display text-3xl md:text-4xl text-navy-900">
                {s.prefix}
                <Counter value={s.value} />
                {s.suffix}
              </div>
              <div className="text-xs md:text-sm mt-1 text-navy-500 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </Reveal>
      </section>

      {/* OFFRE / SERVICES */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/4 left-0 w-72 h-72 md:w-96 md:h-96 bg-coral-400/8 blur-[60px] rounded-full"></div>
        <div className="absolute bottom-1/4 right-0 w-80 h-80 md:w-[26rem] md:h-[26rem] bg-slate-300/25 blur-[70px] rounded-full"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[28rem] h-[28rem] md:w-[40rem] md:h-[40rem] bg-slate-200/20 blur-[80px] rounded-full"></div>
        <Reveal className="relative mx-auto max-w-6xl px-6 md:px-10">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.2em] uppercase text-coral-600 font-semibold">Ce que l'on fait</p>
            <h2 className="font-display text-3xl md:text-5xl text-navy-900 mt-4 leading-tight">
              Une offre claire, pensée pour les commerçants.
            </h2>
            <p className="mt-5 text-navy-600 text-lg leading-relaxed">
              Un site vitrine complet, soigné et mis en place rapidement.
              L'essentiel bien pensé pour mettre en valeur votre savoir-faire,
              être visible sur internet et permettre à vos clients de vous
              retrouver facilement grâce à vos coordonnées.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div
                key={f.title}
                className="group bg-slate-300/70 backdrop-blur-xl border border-white/40 rounded-xl p-7 hover:border-coral-300/70 hover:bg-slate-300/80 hover:-translate-y-1 hover:scale-[1.02] hover:shadow-xl hover:shadow-navy-900/10 transition-all duration-300 cursor-default relative"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-navy-900 text-coral-300 group-hover:bg-coral-400 group-hover:text-navy-900 transition-colors">
                  <i className={`${f.icon} text-2xl`}></i>
                </div>
                <h3 className="mt-5 font-semibold text-navy-900 text-lg">{f.title}</h3>
                <p className="mt-2 text-sm text-navy-600 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* PRICING */}
      <section className="relative py-24 bg-navy-900 text-white overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-coral-600/8 blur-[60px] rounded-[60%_40%_50%_50%]"></div>
        <div className="absolute top-1/2 right-0 translate-x-1/4 w-80 h-80 bg-coral-700/6 blur-[70px] rounded-[50%_50%_40%_60%]"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-coral-400/4 blur-3xl rounded-[40%_60%_60%_40%]"></div>

        <Reveal className="relative mx-auto max-w-6xl px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-coral-300 font-semibold">Nos tarifs</p>
              <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight">
                Une offre claire, sans surprise.
              </h2>
              <p className="mt-5 text-white text-lg leading-relaxed max-w-lg">
                Un prix unique pour la création, un abonnement annuel abordable pour
                l'hébergement. Vous savez exactement ce que vous payez, dès le départ.
              </p>
              <ul className="mt-8 space-y-3 text-white">
                {[
                  "Page sur-mesure selon vos besoins",
                  "Affichage parfait sur mobile, tablette et ordinateur",
                  "Maquette validée avant intégration",
                  "Formulaire de contact intégré",
                  "Nom de domaine & certificat SSL",
                  "Sauvegardes & mises à jour",
                ].map((i) => (
                  <li key={i} className="flex items-start gap-3">
                    <i className="ri-check-line text-coral-300 mt-1"></i>
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative rounded-2xl p-10 border border-white/20 backdrop-blur-xl bg-gradient-to-br from-navy-800/70 via-navy-800/45 to-navy-700/55 text-white svw-glow-card cursor-pointer">
              <Link
                to="/contact"
                aria-label="Voir l'offre complète et demander mon site"
                className="absolute inset-0 z-0 rounded-2xl"
              />
              <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-coral-400 text-navy-900 text-xs font-semibold uppercase tracking-wider">
                Formule complète
              </div>
              <h3 className="font-display text-2xl mt-2 text-white">Site vitrine ShopVitrineWeb</h3>
              <p className="text-sm text-white mt-1">Tout ce qu'il faut pour bien démarrer.</p>

              <div className="mt-8 pb-8 border-b border-white/15">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-6xl text-white">497,99€</span>
                  <span className="text-white">/ création</span>
                </div>
                <p className="text-xs text-white mt-1">Paiement en 2 fois possible</p>
              </div>

              <div className="mt-8">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl text-white">49,99€</span>
                  <span className="text-white">/ an d'hébergement</span>
                </div>
                <p className="text-xs text-white mt-1">Domaine, SSL, sauvegardes inclus</p>
              </div>

              <div className="mt-6 rounded-xl bg-coral-400/15 border border-coral-300/40 p-5">
                <div className="flex items-start gap-4">
                  <span className="svw-pulse shrink-0 inline-flex items-center gap-1.5 rounded-full coral-shine bg-coral-400 text-navy-900 text-sm font-bold uppercase tracking-wide px-4 py-2 whitespace-nowrap">
                    <i className="ri-gift-line text-base"></i>
                    Offert
                  </span>
                  <p className="text-sm text-white leading-relaxed">
                    Pour toute souscription d'un site avec hébergement, <span className="font-semibold text-white">QR code pour avis Google offert</span> d'une valeur de <span className="font-semibold text-white">29,99 €</span>.
                  </p>
                </div>
              </div>

              <Link
                to="/contact"
                className="mt-10 relative z-10 inline-flex w-full items-center justify-center gap-2 rounded-md coral-shine bg-coral-400 hover:bg-coral-500 text-navy-900 font-semibold px-4 py-2 text-sm whitespace-nowrap cursor-pointer transition-colors"
              >
                Je lance mon projet
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-navy-50">
        <Reveal className="mx-auto max-w-6xl px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs tracking-[0.2em] uppercase text-coral-600 font-semibold">Notre méthode</p>
            <h2 className="font-display text-3xl md:text-5xl text-navy-900 mt-4">
              4 étapes, zéro complication.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((s) => (
              <div key={s.n} className="bg-slate-300/70 backdrop-blur-xl border border-white/40 rounded-xl p-7 hover:border-coral-300/70 hover:bg-slate-300/80 hover:-translate-y-1 transition-all relative">
                <span className="font-display text-4xl text-coral-600">{s.n}</span>
                <h3 className="mt-3 font-semibold text-navy-900">{s.t}</h3>
                <p className="mt-2 text-sm text-navy-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ASSISTANT IA */}
      <section id="assistant-ia" className="py-24 bg-white relative overflow-hidden scroll-mt-20">
        <div className="absolute top-0 right-0 w-96 h-96 bg-coral-400/8 blur-[70px] rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-slate-200/40 blur-[60px] rounded-full"></div>
        <Reveal className="relative mx-auto max-w-6xl px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-coral-600 font-semibold">Votre assistant IA</p>
              <h2 className="font-display text-3xl md:text-5xl text-navy-900 mt-4 leading-tight">
                Une réponse à vos clients,
                <br />
                <span className="text-coral-600">24h/24h.</span>
              </h2>
              <p className="mt-5 text-navy-600 text-lg leading-relaxed text-justify">
                Une TPE qui met en place un assistant IA économise en moyenne jusqu'à
                30 % de son temps de support client (source : Bpifrance). Plus largement,
                les entreprises qui intègrent des agents IA dans leurs processus clés
                observent des gains de productivité compris entre 15 % et 30 %
                (source : Angle Formation, citant Bpifrance).
              </p>
              <p className="mt-5 text-navy-600 text-lg leading-relaxed text-justify">
                Et derrière ces chiffres, il y a une réalité simple : un client qui n'a
                pas de réponse rapide va souvent voir ailleurs. Avec votre assistant IA
                disponible <span className="font-semibold text-navy-800">24h/24h</span>,
                vos clients reçoivent une réponse immédiate, même la nuit, le dimanche ou
                pendant les fermetures. Il prend aussi les rendez-vous automatiquement,
                sans que vous ayez à décrocher le téléphone entre deux clients, et il
                répond aux questions les plus fréquentes (horaires, tarifs, disponibilité)
                pour vous libérer du temps à consacrer à des tâches à plus grande valeur ajoutée.
              </p>
              <div className="mt-9">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-md coral-shine bg-coral-400 hover:bg-coral-500 text-navy-900 font-semibold px-5 py-2.5 text-sm whitespace-nowrap cursor-pointer transition-colors"
                >
                  Intéressé par un agent IA pour votre commerce ? Cliquez ici
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <p className="mt-3 text-xs text-navy-500">
                  Le tarif de l'agent IA dépend de votre projet.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl border border-navy-100 bg-white/80 backdrop-blur-xl p-7 md:p-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-navy-900 text-coral-300">
                    <i className="ri-robot-2-line text-2xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900">Shopy</p>
                    <p className="text-xs text-navy-500 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      24h/24h
                    </p>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="max-w-[80%] ml-auto bg-coral-400 text-navy-900 rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
                    Bonjour, êtes-vous ouvert un dimanche ?
                  </div>
                  <div className="max-w-[85%] bg-navy-50 text-navy-800 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm">
                    Bonjour, je suis Shopy, votre assistant. Je suis là pour vous répondre à tout moment. Nous sommes fermés le dimanche, mais n'hésitez pas à me poser vos questions.
                  </div>
                  <div className="max-w-[80%] ml-auto bg-coral-400 text-navy-900 rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
                    D'accord, je voudrais un rendez-vous cette semaine.
                  </div>
                  <div className="max-w-[85%] bg-navy-50 text-navy-800 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm">
                    Bien sûr ! Cliquez ici pour choisir votre créneau directement dans notre agenda.
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-2 rounded-full border border-navy-100 bg-white px-4 py-2">
                  <span className="text-sm text-navy-400">Posez votre question…</span>
                  <span className="ml-auto w-7 h-7 flex items-center justify-center rounded-full bg-coral-400 text-navy-900">
                    <i className="ri-send-plane-fill text-sm"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-navy-50">
        <Reveal className="mx-auto max-w-4xl px-6 md:px-10">
          <div className="text-center">
            <p className="text-xs tracking-[0.2em] uppercase text-coral-600 font-semibold">Questions fréquentes</p>
            <h2 className="font-display text-3xl md:text-5xl text-navy-900 mt-4">
              Tout ce que vous voulez savoir.
            </h2>
          </div>
          <div className="mt-12 space-y-4">
            {faq.map((f, i) => (
              <details
                key={i}
                className="group bg-white rounded-xl border border-navy-100 p-6 cursor-pointer"
              >
                <summary className="flex items-center justify-between font-semibold text-navy-900 list-none">
                  <span>{f.q}</span>
                  <i className="ri-add-line text-coral-700 text-2xl group-open:rotate-45 transition-transform"></i>
                </summary>
                <p className="mt-3 text-navy-600 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy-900 text-white">
        <Reveal className="mx-auto max-w-4xl px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl leading-tight">
            Prêt à donner à votre commerce
            <br />
            <span className="text-coral-300">la vitrine qu'il mérite ?</span>
          </h2>
          <p className="mt-5 text-white max-w-2xl mx-auto">
            On échange gratuitement pour comprendre votre projet et vous proposer un
            devis clair sous 48 heures.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md coral-shine bg-coral-400 hover:bg-coral-500 text-navy-900 font-semibold px-4 py-2 text-sm whitespace-nowrap cursor-pointer transition-colors"
            >
              Prendre contact
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href="mailto:contact@shopvitrineweb.fr"
              className="inline-flex items-center gap-2 text-white hover:text-coral-300 font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              contact@shopvitrineweb.fr
            </a>
            <a
              href="https://wa.me/33763297694"
              target="_blank"
              rel="nofollow noopener noreferrer"
              aria-label="Discuter sur WhatsApp"
              title="Discuter sur WhatsApp"
              className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#25D366] hover:brightness-110 text-white cursor-pointer transition-all"
            >
              <i className="ri-whatsapp-fill text-2xl"></i>
            </a>
          </div>
        </Reveal>
      </section>
    </Layout>
  );
}